import type { ReactNode } from 'react';

/**
 * Where a business is on the way to running its day with us — four steps
 * across the top of its page (docs/console-redesign-plan.md §2, "Stages").
 *
 * Presentational only: the caller decides which step is `current` and what
 * each one is called. `current` may be `-1`, which is a business *off* the
 * track — paused or offboarded are states, not steps, and drawing them as a
 * fifth and sixth box would say they come after "Live" in a journey, which
 * they do not.
 *
 * Steps before `current` read as done, the current one is highlighted and
 * announced with `aria-current="step"`, and the ones after are plain.
 */
export interface StageStep {
  key: string;
  /** "Stage 1", "Later" — the small line above the name. */
  caption: string;
  name: string;
  /** What the step is made of, in a few words: "clients and appointments". */
  hint: string;
}

export function StageTrack({
  label,
  steps,
  current,
  nowLabel,
}: {
  label: string;
  steps: readonly StageStep[];
  /** Index into `steps`, or -1 for a business that is off the track. */
  current: number;
  /** Appended to the current step's caption: "Stage 1 · now". */
  nowLabel: string;
}): ReactNode {
  return (
    <ol className="journey" aria-label={label}>
      {steps.map((step, i) => {
        const state = i === current ? 'here' : current >= 0 && i < current ? 'done' : '';
        return (
          <li
            key={step.key}
            className={`journey__step${state === '' ? '' : ` is-${state}`}`}
            aria-current={i === current ? 'step' : undefined}
          >
            <small>{i === current ? `${step.caption} · ${nowLabel}` : step.caption}</small>
            <strong>{step.name}</strong>
            <small>{step.hint}</small>
          </li>
        );
      })}
    </ol>
  );
}
